import React, { useEffect, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import moment from "moment";
import SelectFiled from "../../common/textfield/SelectFiled";
import { useFormik } from "formik";
import * as Yup from "yup";
import { failed } from "../../common/Toastify";
import { checkSchedule, getSlots } from "../../apps/api/AppointmentRequests";
import { Col, Row } from "react-bootstrap";
import { Skeleton } from "@mui/material";
import { ViewDate } from "../../../utils/DateSupport";
import {getScheduleSlots} from "../../api/Schedule";
import { useSelector } from "react-redux";
import { validation, conditionalValidation } from "../../../utils/constants";
import {
    filterSlotsForSelectedDay,
    getDateKey,
    getScheduleDateOptions,
    scheduleMatchesSelectedOrg,
    sortSlotsByStart,
} from "../../../utils/ScheduleAvailabilitySupport";

const normalizeScheduleData = (data) => (Array.isArray(data) ? data : [data]).filter(Boolean);

const getServiceCategoryId = (category) => category?.id || category?.coding?.[0]?.code;

const getServiceCategoryName = (category) => category?.name || category?.coding?.[0]?.display;

const getErrorMessage = (res) => res?.response?.data?.message || res?.response?.data?.error || res?.message;

export function ViewScheduleDayModal({ modalShow, handleShow, userID, }) {
    const [serviceCategory, setServiceCategory] = useState([]);
    const [reservedCategories, setReservedCategories] = useState ({})
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const userOrg = useSelector((state) => state?.auth?.user?.organizations);
    const userOrgId = userOrg?.[0]?.id;

    const formik = useFormik({
        initialValues: {
            serviceCategory: "",
        },
        validationSchema: Yup.object({
            serviceCategory: conditionalValidation(
                Yup.string(),
                {
                    validation,
                    mandatory: true,
                    label: "Title"
                })
        }),
    });

    useEffect(() => {
        let isMounted = true;
        const selectedDayKey = getDateKey(modalShow);
        const selectedDateParam = moment(modalShow)?.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";

        const loadServiceCategories = async () => {
            setSlotsLoading(true);
            setAvailableSlots([]);

            try {
                const res = await checkSchedule({ actorId: userID, date: selectedDateParam, orgId: userOrgId});
                if(!res?.data) {
                    if (isMounted) {
                        failed(res?.message);
                        setServiceCategory([]);
                        setReservedCategories({});
                    }
                    return;
                }

                const schedules = normalizeScheduleData(res.data)
                    .filter((schedule) => scheduleMatchesSelectedOrg(schedule, userOrgId));
                const serviceCategoriesById = new Map();

                schedules.forEach((schedule) => {
                    (schedule.serviceCategory || []).forEach((categoryData) => {
                        const categoryId = categoryData?.id;
                        if (!categoryId || serviceCategoriesById.has(categoryId)) return;
                        if (getDateKey(categoryData?.date, getScheduleDateOptions(categoryData?.date)) !== selectedDayKey) return;

                        serviceCategoriesById.set(categoryId, {
                            ...categoryData,
                            value: categoryId,
                        });
                    });
                });

                const reservedSlots = {};
                await Promise.all(schedules.map(async (schedule) => {
                    const resp = await getScheduleSlots(schedule.id, "busy-reserved", userOrgId);

                    (resp.data || []).forEach((slot) => {
                        (slot?.serviceCategory || []).forEach((slotCategory) => {
                            const serviceCategoryId = getServiceCategoryId(slotCategory);
                            if (!serviceCategoryId) return;

                            const categoryDate = schedule.serviceCategory?.find((item) => item.id === serviceCategoryId)?.date;
                            if (getDateKey(slot?.start, getScheduleDateOptions(categoryDate)) !== selectedDayKey) return;

                            const reservedSlot = {
                                "start": slot?.start,
                                "end": slot?.end,
                                "id": slot?.id,
                                "categoryDate": categoryDate,
                                "serviceCategory": [slotCategory],
                                "status": "busy-reserved"
                            };

                            if (reservedSlots.hasOwnProperty(serviceCategoryId)) {
                                reservedSlots[serviceCategoryId].push(reservedSlot);
                            } else {
                                reservedSlots[serviceCategoryId] = [reservedSlot];
                            }
                        });
                    });
                }));

                const availableServiceCategories = (await Promise.all(
                    [...serviceCategoriesById.values()].map(async (category) => {
                        try {
                            const slotsResponse = await getSlots({
                                actorId: userID,
                                date: selectedDateParam,
                                serviceCategoryId: category.value,
                                orgId: userOrgId
                            });
                            const slots = sortSlotsByStart(filterSlotsForSelectedDay(
                                slotsResponse?.data?.availableSlots || [],
                                selectedDayKey,
                                { options: getScheduleDateOptions(category?.date) }
                            ));

                            return slots.length ? { category, slots } : null;
                        } catch (res) {
                            const message = getErrorMessage(res);
                            if (message?.toLowerCase()?.includes("no slots")) {
                                return null;
                            }
                            throw res;
                        }
                    })
                )).filter(Boolean);

                const reservedServiceCategories = Object.keys(reservedSlots).map((key) => {
                    const firstSlot = reservedSlots[key]?.[0];
                    const firstCategory = firstSlot?.serviceCategory?.[0];

                    return {
                        name: `Reserved for ${getServiceCategoryName(firstCategory)}`,
                        value: `${key}-busyReserved`,
                        date: firstSlot?.categoryDate || firstSlot?.start,
                        status: "busy-reserved"
                    };
                });

                const nextServiceCategories = [
                    ...availableServiceCategories.map(({ category }) => category),
                    ...reservedServiceCategories,
                ].sort((a, b) => a.name > b.name ? 1 : -1);
                const firstCategory = nextServiceCategories[0];

                if (!isMounted) return;

                setReservedCategories(reservedSlots);
                setServiceCategory(nextServiceCategories);
                formik.setFieldValue("serviceCategory", firstCategory?.value || "");

                if (!firstCategory) {
                    setAvailableSlots([]);
                } else if (firstCategory.status === "busy-reserved") {
                    const reservedKey = firstCategory.value.replace(/-busyReserved$/, "");
                    const slots = filterSlotsForSelectedDay(reservedSlots[reservedKey] || [], selectedDayKey, {
                        options: getScheduleDateOptions(firstCategory?.date),
                    });
                    setAvailableSlots(sortSlotsByStart(slots));
                } else {
                    const selectedCategorySlots = availableServiceCategories.find(
                        ({ category }) => category.value === firstCategory.value
                    )?.slots || [];
                    setAvailableSlots(selectedCategorySlots);
                }
            } catch (res) {
                if (isMounted) {
                    failed(getErrorMessage(res));
                    setServiceCategory([]);
                    setReservedCategories({});
                }
            } finally {
                if (isMounted) {
                    setSlotsLoading(false);
                }
            }
        };

        loadServiceCategories();

        return () => {
            isMounted = false;
        };
    }, [modalShow, userID, userOrgId])

    const sortedObj = data => {
        return data.sort((a,b) => {
            return a.name > b.name ? 1 : -1
        });
    }

    const handleServiceCategory = (event) => {
        event && formik.setFieldValue("serviceCategory", event?.target?.value);
        setSlotsLoading(true)
        let targetVal = event?.target?.value;
        const selectedCategory = serviceCategory.find(
            (item) => item.value === targetVal || item.value === `${targetVal}-busyReserved`
        );
        const scheduleDateOptions = getScheduleDateOptions(selectedCategory?.date);
        if(targetVal?.endsWith("busyReserved")){
            let slots = reservedCategories[event?.target?.value.replace(/-busyReserved$/, "")] || [];
            slots = filterSlotsForSelectedDay(slots, getDateKey(modalShow), { options: scheduleDateOptions });
            slots = sortSlotsByStart(slots)
                //.filter(slot => moment(slot.start).isAfter(currentTime))
                //.sort((a, b) => moment(a.start).diff(moment(b.start)));
    
            setAvailableSlots(slots)
            setSlotsLoading(false)
        }else if(targetVal) {
            getSlots({ actorId: userID, date: moment(modalShow).utc().format("YYYY-MM-DDTHH:mm:ss") + "Z", serviceCategoryId: event?.target?.value || "default", orgId: userOrgId })
                .then((res) => {
                    let slots = filterSlotsForSelectedDay(res?.data?.availableSlots || [], getDateKey(modalShow), {
                        options: scheduleDateOptions,
                    });
                    slots = sortSlotsByStart(slots);
                    setAvailableSlots(slots)
                }).catch((res) => {
                    const message = getErrorMessage(res);
                    if (!message?.toLowerCase()?.includes("no slots")) {
                        failed(message);
                    }
                    setAvailableSlots([]);
                }).finally(() => { setSlotsLoading(false) })
        }
        else setSlotsLoading(false)
    };

    return (
        <>
            <Modal
                size="md" show={modalShow}
                onHide={handleShow}
                aria-labelledby="contained-modal-title-vcenter"
                backdropClassName
                backdrop={'static'}
                centered className="custom-dialog">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title id="contained-modal-title-vcenter" style={{ textTransform: "capitalize" }}>
                        {ViewDate(modalShow)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
                        <SelectFiled keyField={"serviceCategory"} label={"Select Service Category"} formik={formik} options={sortedObj(serviceCategory)} onChange={handleServiceCategory} />

                        {serviceCategory?.length > 0 && availableSlots?.length > 0 ?
                            <>
                                <Row>
                                    <Col>Select Slot</Col>
                                </Row>
                                <Row style={{ justifyContent: "center", rowGap: "0.5rem", marginBottom: "1.5rem", marginTop: "0.5rem" }}>
                                    {availableSlots?.map((slot, index) =>{
                                    let time;
                                    let localTime;
                                    if(slot?.status === "busy-reserved"){
                                        localTime = moment(slot?.start).format("HH:mm");
                                    }else{
                                        //time=`${moment(formik?.values?.date)?.format("YYYY-MM-DD")}T${(slot?.start)}:00Z`;
                                        localTime = moment(slot?.start).format("HH:mm");
                                    }
                                    return (<Col sm={2} className="slotStyle" key={index} onClick={() => { formik?.setFieldValue("availableSlot", slot); }}>{localTime}</Col>)})
                                    }
                                </Row>
                            </>
                            : null}
                        {slotsLoading ?
                            <>
                                <Row>
                                    <Col>Select Slot</Col>
                                </Row>
                                <Skeleton height={50} />
                            </>
                            : null}
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
}
